import { Plugin } from "prosemirror-state";
import type { EditorView } from "prosemirror-view";
import { isJust, toMaybe } from "../../lib/maybe";

const HIGHLIGHT_NAME = "zw-prose-selection";

/** Paint without splitting text nodes: selection-dependent inline decorations
 * can reverse Safari's selection direction and interfere with native editing. */
export function paintedSelectionPlugin(): Plugin {
  // Older webviews keep their native selection instead of hiding it.
  if (typeof Highlight === "undefined" || "highlights" in CSS === false) {
    return new Plugin({});
  }

  return new Plugin({
    props: {
      attributes: ({ selection }) =>
        selection.empty === false && selection.visible
          ? { class: "zw-prose-painted-selection" }
          : {},
    },
    view(view) {
      const range = view.dom.ownerDocument.createRange();

      function clear(): void {
        const highlight = toMaybe(CSS.highlights.get(HIGHLIGHT_NAME));
        if (isJust(highlight)) {
          highlight.value.delete(range);
          if (highlight.value.size === 0) CSS.highlights.delete(HIGHLIGHT_NAME);
        }
      }

      function update(current: EditorView): void {
        clear();
        const { from, to, empty, visible } = current.state.selection;
        if (empty || visible === false) return;
        const start = current.domAtPos(from, 1);
        const end = current.domAtPos(to, -1);
        range.setStart(start.node, start.offset);
        range.setEnd(end.node, end.offset);
        // Several editor surfaces can coexist. Each owns only its own range.
        const highlight = CSS.highlights.get(HIGHLIGHT_NAME) ?? new Highlight();
        highlight.add(range);
        CSS.highlights.set(HIGHLIGHT_NAME, highlight);
      }

      update(view);
      return { update, destroy: clear };
    },
  });
}
